import React, { useEffect, useState } from 'react';
import VehicleList from './VehicleList';
import VehicleForm from './VehicleForm';
import TrailerList from './TrailerList';
import TrailerForm from './TrailerForm';
import { Vehicle, Trailer } from '../../../types/saas';
import saasApiService from '../../../services/saasApi';
import { AlertTriangle, Bell } from 'lucide-react';

const VehicleManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'vehicles' | 'trailers'>('vehicles');
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [editingTrailer, setEditingTrailer] = useState<Trailer | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setAlertsLoading(true);
        const res = await saasApiService.getVehicleAlerts();
        // Normalizar posibles formas de respuesta
        const list = Array.isArray(res) ? res : (res?.alerts || res?.data?.alerts || res?.data || []);
        setAlerts(Array.isArray(list) ? list : []);
      } catch (e) {
        setAlerts([]);
      } finally {
        setAlertsLoading(false);
      }
    })();
  }, [refreshTrigger]);

  const handleAdd = () => {
    if (activeTab === 'vehicles') {
      setEditingVehicle(null);
      setShowForm(true);
    } else {
      setEditingTrailer(null);
      setShowForm(true);
    }
  };

  const handleEdit = (item: any) => {
    if (activeTab === 'vehicles') {
      setEditingVehicle(item as Vehicle);
    } else {
      setEditingTrailer(item as Trailer);
    }
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingVehicle(null);
    setEditingTrailer(null);
  };

  const handleSave = () => {
    console.log('VehicleManager: handleSave called, triggering refresh');
    setRefreshTrigger(prev => {
      const newValue = prev + 1;
      console.log('VehicleManager: refreshTrigger updated to:', newValue);
      return newValue;
    });
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('vehicles')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'vehicles' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Vehículos
          </button>
          <button
            onClick={() => setActiveTab('trailers')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'trailers' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Remolques
          </button>
        </nav>
      </div>

      {activeTab === 'vehicles' ? (
        <>
          {/* Vehicle Alerts */}
          <div className="bg-white border rounded-md p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-yellow-600" />
                <h3 className="text-sm font-semibold text-gray-800">Alertas de Vehículos</h3>
              </div>
              {alertsLoading && (
                <div className="text-xs text-gray-500">Cargando…</div>
              )}
            </div>
            {(!alerts || alerts.length === 0) ? (
              <div className="mt-2 text-xs text-gray-500">No hay alertas por el momento.</div>
            ) : (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {alerts.map((a: any, idx: number) => (
                  <div key={idx} className="flex items-start gap-2 border rounded-md p-3 bg-yellow-50 border-yellow-200">
                    <AlertTriangle className="h-4 w-4 text-yellow-700 mt-0.5" />
                    <div className="text-xs text-yellow-800">
                      <div className="font-semibold">{a.title || a.type || 'Alerta'}</div>
                      <div className="mt-0.5">
                        {a.message || a.description || `${a.vehicle_info || ''} ${a.detail || ''}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <VehicleList onEdit={handleEdit as any} onAdd={handleAdd} refreshTrigger={refreshTrigger} />
          {showForm && (
            <VehicleForm vehicle={editingVehicle} onClose={handleClose} onSave={handleSave} />
          )}
        </>
      ) : (
        <>
          <TrailerList onEdit={handleEdit as any} onAdd={handleAdd} refreshTrigger={refreshTrigger} />
          {showForm && (
            <TrailerForm trailer={editingTrailer as any} onClose={handleClose} onSave={handleSave} />
          )}
        </>
      )}
    </div>
  );
};

export default VehicleManager;
